import { useCallback, useEffect, useState } from 'react';
import { api, ApiError } from '../../api/index.js';
import { useToast } from '../ui/toast.jsx';

/**
 * KMS LICENSE widget for the System tab.
 *
 * Mirrors screensot-1.png from manuals/kms-integration:
 *   - status pill (Active / Active w/ warning / restrictive variants)
 *   - last-checked stamp
 *   - License ID / Software UUID with copy buttons
 *   - Hardware match + vCPU
 *   - Maintenance Until + Expires At cards (with day-remaining colored chip)
 *     — falls back to ∞ Lifetime when expires_at is missing (per spec §4.1)
 *   - footer: ad-kms-service build + trusted-key role + short FP
 *   - Refresh / Renew license buttons (Renew is admin-only)
 *
 * Status sources:
 *   - On mount we kick off /api/license/info (heavy) so the widget gets the
 *     extended fields (vcpu, build, trusted_key_role, token).
 *   - We also poll /api/license/status every 10s so day-remaining counters
 *     and warnings stay live without re-fetching the token.
 *   - Refresh button → POST /api/license/refresh (one-shot daemon poll).
 *   - Renew button   → POST /api/license/renew  (full KMS handshake).
 */

const POLL_MS = 10_000;

const STATUS_META = {
  active:                       { label: 'Active',                 tone: 'good',    icon: 'fa-circle-check' },
  active_with_warning:          { label: 'Active (warning)',       tone: 'warn',    icon: 'fa-triangle-exclamation' },
  time_unsynced:                { label: 'Time not synced',        tone: 'warn',    icon: 'fa-clock-rotate-left' },
  initializing:                 { label: 'Initializing…',          tone: 'mute',    icon: 'fa-circle-notch fa-spin' },
  not_activated:                { label: 'Not activated',          tone: 'bad',     icon: 'fa-circle-xmark' },
  not_configured:               { label: 'Not configured',         tone: 'bad',     icon: 'fa-gears' },
  ipc_unreachable:              { label: 'IPC unreachable',        tone: 'bad',     icon: 'fa-plug-circle-xmark' },
  expired:                      { label: 'Expired',                tone: 'bad',     icon: 'fa-hourglass-end' },
  revoked:                      { label: 'Revoked',                tone: 'bad',     icon: 'fa-ban' },
  hardware_mismatch:            { label: 'Hardware mismatch',      tone: 'bad',     icon: 'fa-microchip' },
  software_uuid_mismatch:       { label: 'Software UUID mismatch', tone: 'bad',     icon: 'fa-fingerprint' },
  signature_invalid:            { label: 'Signature invalid',      tone: 'bad',     icon: 'fa-shield-halved' },
  tampering_detected:           { label: 'Tampering detected',     tone: 'bad',     icon: 'fa-shield-virus' },
  unsupported_token_version:    { label: 'Unsupported version',    tone: 'bad',     icon: 'fa-code-branch' },
  unsupported_crypto_suite:     { label: 'Unsupported crypto',     tone: 'bad',     icon: 'fa-key' },
};

const TONE_CLASSES = {
  good: 'border-emerald-200 bg-emerald-50  text-emerald-700',
  warn: 'border-amber-200   bg-amber-50    text-amber-700',
  bad:  'border-rose-200    bg-rose-soft   text-rose-700',
  mute: 'border-line        bg-canvas      text-mist',
};

const DAYS_TONE = (n) => {
  if (n == null) return TONE_CLASSES.mute;
  if (n <= 0)    return TONE_CLASSES.bad;
  if (n < 30)    return TONE_CLASSES.warn;
  return TONE_CLASSES.good;
};

function formatUtc(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  // Mirror screenshot: "03 May 2026, 11:35:13 UTC"
  const opts = { day: '2-digit', month: 'short', year: 'numeric',
                 hour: '2-digit', minute: '2-digit', second: '2-digit',
                 timeZone: 'UTC', hour12: false };
  return d.toLocaleString('en-GB', opts).replace(',', ',') + ' UTC';
}

function shortFp(fp) {
  if (!fp) return null;
  // Strip optional "ecdsa-p384:" prefix the daemon may include.
  let raw = String(fp).split(':').pop() || String(fp);
  // Already colon-grouped 8-byte form? Take first 6 bytes for compact view.
  if (raw.includes(':')) {
    const groups = raw.split(':').slice(0, 6).join(':');
    return groups.toLowerCase();
  }
  // Hex-only form — chunk into colon-separated bytes (first 6 bytes).
  raw = raw.replace(/[^0-9a-fA-F]/g, '');
  const bytes = raw.match(/.{1,2}/g) || [];
  return bytes.slice(0, 6).join(':').toLowerCase();
}

export function LicenseWidget({ currentUser }) {
  const toast = useToast();
  const isAdmin = currentUser?.role === 'admin';

  const [license, setLicense] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [renewing, setRenewing] = useState(false);

  // Initial load: pull the heavy snapshot once (token / build / trusted key).
  // After that the lightweight poll keeps it fresh.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // info() requires admin; non-admin operators just get the cached
        // snapshot via /status which is enough for the read-only widget.
        const r = isAdmin ? await api.license.info() : await api.license.status();
        if (!cancelled) setLicense(r.license);
      } catch (err) {
        if (cancelled) return;
        if (err instanceof ApiError && err.status === 403) {
          // Non-admin without info perms — fall back to /status
          try {
            const r = await api.license.status();
            if (!cancelled) setLicense(r.license);
          } catch (e2) {
            if (!cancelled) setLoadError(e2?.payload?.error || e2.message);
          }
        } else {
          setLoadError(err?.payload?.error || err.message);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [isAdmin]);

  // Background polling — cheap, in-memory snapshot on the server.
  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      try {
        const r = await api.license.status();
        if (!cancelled) {
          // Preserve fields that only get_license_info populates (token/build/etc).
          setLicense((prev) => ({ ...(prev || {}), ...r.license }));
        }
      } catch { /* silent — banner will show next time it polls */ }
    };
    const id = setInterval(tick, POLL_MS);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    try {
      const r = await api.license.refresh();
      setLicense(r.license);
      toast.show({ kind: 'success', message: 'License snapshot refreshed.' });
    } catch (err) {
      toast.show({
        kind: 'error',
        message: err?.payload?.error || err.message || 'Refresh failed.',
      });
    } finally {
      setRefreshing(false);
    }
  }, [toast]);

  const handleRenew = useCallback(async () => {
    setRenewing(true);
    try {
      const r = await api.license.renew();
      setLicense(r.license);
      if (r.result?.ok) {
        toast.show({
          kind: 'success',
          message: `License renewed. Maintenance: ${formatUtc(r.result.maintenance_until) || 'updated'}`,
        });
      } else {
        toast.show({
          kind: 'error',
          message: 'Renew failed: ' + (r.result?.error || 'unknown error'),
        });
      }
    } catch (err) {
      toast.show({
        kind: 'error',
        message: err?.payload?.error || err.message || 'Renew failed.',
      });
    } finally {
      setRenewing(false);
    }
  }, [toast]);

  if (loadError) {
    return (
      <div className="card border-rose-200 bg-rose-soft px-6 py-4 text-sm text-rose-700">
        <i className="fa-solid fa-circle-exclamation mr-2" aria-hidden="true" />
        {loadError}
      </div>
    );
  }

  if (!license) {
    return (
      <div className="card px-6 py-12 text-center text-mist">
        <span className="loader-spinner mx-auto mb-3 text-accent" aria-hidden="true" />
        <div className="text-sm">Loading license status…</div>
      </div>
    );
  }

  const meta = STATUS_META[license.status] || { label: license.status || 'Unknown', tone: 'mute', icon: 'fa-circle-question' };
  const fp = shortFp(license.trustedKeyFingerprint);

  return (
    <div className="card">
      <header className="flex flex-col gap-3 border-b border-line px-6 py-4 md:flex-row md:items-start md:justify-between">
        <div className="flex items-start gap-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-accent-soft text-accent">
            <i className="fa-solid fa-shield-halved" aria-hidden="true" />
          </div>
          <div>
            <h2 className="text-[11px] font-bold uppercase tracking-[0.1em] text-mist">KMS License</h2>
            <p className="text-sm text-ink">
              For license renewals, migrations, maintenance extensions, and updates, please contact:{' '}
              <a href="mailto:support@micro-gis.com" className="text-accent hover:underline">
                support@micro-gis.com
              </a>
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button type="button" onClick={handleRefresh} disabled={refreshing} className="btn-secondary">
            <i className={`fa-solid fa-arrows-rotate ${refreshing ? 'animate-spin' : ''}`} aria-hidden="true" />
            Refresh
          </button>
          {isAdmin && (
            <button type="button" onClick={handleRenew} disabled={renewing} className="btn-primary">
              {renewing ? (
                <><span className="loader-spinner" aria-hidden="true" /> Renewing…</>
              ) : (
                <><i className="fa-solid fa-arrows-rotate" aria-hidden="true" /> Renew license</>
              )}
            </button>
          )}
        </div>
      </header>

      <div className="space-y-5 px-6 py-5">
        {/* Status pill + last checked */}
        <div className="flex flex-wrap items-center gap-3">
          <span className={`inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs font-semibold ${TONE_CLASSES[meta.tone]}`}>
            <i className={`fa-solid ${meta.icon}`} aria-hidden="true" />
            {meta.label}
          </span>
          {license.lastCheckedAt && (
            <span className="text-xs text-mist">
              Last checked: <strong className="text-ink">{formatUtc(license.lastCheckedAt)}</strong>
              <span className="ml-2 text-[10px] uppercase tracking-wider text-mist">via {license.pollSource || 'poll'}</span>
            </span>
          )}
          {license.reason && (
            <span className="text-xs text-rose-600">
              <i className="fa-solid fa-circle-info mr-1" aria-hidden="true" /> {license.reason}
            </span>
          )}
        </div>

        {/* Warnings list */}
        {Array.isArray(license.warnings) && license.warnings.length > 0 && (
          <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
            <i className="fa-solid fa-triangle-exclamation mr-2" aria-hidden="true" />
            <strong>Warnings:</strong>
            <ul className="ml-6 mt-1 list-disc">
              {license.warnings.map((w, i) => <li key={i}>{w}</li>)}
            </ul>
          </div>
        )}

        {/* Identity */}
        <div className="grid grid-cols-1 gap-x-8 gap-y-2 sm:grid-cols-[160px_1fr]">
          <Field label="License ID"    value={license.licenseId}    mono copyable />
          <Field label="Software UUID" value={license.softwareUuid} mono copyable />
          <Field
            label="Hardware match"
            value={
              license.hardwareMatch
                ? (
                    <span className="inline-flex items-center gap-1.5">
                      <code className="font-mono">{license.hardwareMatch}</code>
                      {license.hardwareMatch === '5/5' && (
                        <i className="fa-solid fa-check text-emerald-600" aria-hidden="true" />
                      )}
                    </span>
                  )
                : null
            }
          />
          <Field label="vCPU" value={license.vcpuCount != null ? String(license.vcpuCount) : null} mono />
        </div>

        {/* Maintenance + Expires cards */}
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <DateCard
            icon="fa-screwdriver-wrench"
            label="Maintenance until"
            iso={license.maintenanceUntil}
            days={license.daysUntilMaintenance}
          />
          {license.isLifetime ? (
            <LifetimeCard />
          ) : (
            <DateCard
              icon="fa-hourglass-half"
              label="Expires at"
              iso={license.expiresAt}
              days={license.daysUntilExpiry}
            />
          )}
        </div>

        {/* Footer */}
        <div className="flex flex-wrap items-center gap-x-6 gap-y-2 border-t border-line pt-4 text-xs text-mist">
          {license.build && (
            <span>
              <i className="fa-solid fa-box mr-1.5" aria-hidden="true" />
              ad-kms-service: <code className="ml-1 font-mono text-ink">{license.build}</code>
            </span>
          )}
          {license.trustedKeyRole && (
            <span>
              <i className="fa-solid fa-key mr-1.5" aria-hidden="true" />
              trusted key: <code className="ml-1 font-mono text-ink">{license.trustedKeyRole}</code>
              {fp && <span className="ml-1 text-mist">({fp})</span>}
            </span>
          )}
          {!fp && license.trustedKeyRole && isAdmin && (
            <span className="text-[10px] italic text-mist">
              Click <strong>Renew license</strong> to populate the signing-key fingerprint.
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Sub-components ─────────────────────────────────────────────────

function Field({ label, value, mono, copyable }) {
  if (value == null) {
    return (
      <>
        <div className="text-sm font-medium text-ink">{label}</div>
        <div className="text-sm text-mist">—</div>
      </>
    );
  }
  return (
    <>
      <div className="text-sm font-medium text-ink">{label}</div>
      <div className={`text-sm text-ink ${mono ? 'font-mono' : ''}`}>
        <span className="break-all">{value}</span>
        {copyable && typeof value === 'string' && (
          <button
            type="button"
            className="ml-2 align-middle text-mist hover:text-accent"
            title="Copy"
            onClick={() => navigator.clipboard?.writeText(value)}
          >
            <i className="fa-regular fa-copy" aria-hidden="true" />
          </button>
        )}
      </div>
    </>
  );
}

function DateCard({ icon, label, iso, days }) {
  return (
    <div className="rounded-2xl border border-line bg-canvas/40 px-4 py-3">
      <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.1em] text-mist">
        <i className={`fa-solid ${icon}`} aria-hidden="true" />
        {label}
      </div>
      <div className="mt-1 font-mono text-sm text-ink">{formatUtc(iso) || '—'}</div>
      {days != null && (
        <div className={`mt-2 inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-[11px] font-semibold ${DAYS_TONE(days)}`}>
          <i className="fa-solid fa-hourglass-half" aria-hidden="true" />
          {days <= 0 ? 'expired' : `${days} days remaining`}
        </div>
      )}
    </div>
  );
}

function LifetimeCard() {
  return (
    <div className="rounded-2xl border border-line bg-canvas/40 px-4 py-3">
      <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.1em] text-mist">
        <i className="fa-solid fa-hourglass-half" aria-hidden="true" />
        Expires at
      </div>
      <div className="mt-1 font-mono text-sm text-ink">(lifetime)</div>
      <div className="mt-2 inline-flex items-center gap-1.5 rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-0.5 text-[11px] font-semibold text-emerald-700">
        <i className="fa-solid fa-infinity" aria-hidden="true" />
        Lifetime
      </div>
    </div>
  );
}
